/**
 * A map of all supported environment values.
 * Environments are set through `VUE_APP_ENV` environment variable (note that this is may not match `NODE_ENV`).
 *
 * @private
 * @see https://cli.vuejs.org/guide/mode-and-env.html#environment-variables
 */
const knownEnvironments = Object.freeze({
  development: 'development',
  nonprod: 'nonprod',
  performance: 'performance',
  playground: 'playground',
  production: 'production',
})

/**
 * Checks whether the given `environment` is one of the app's supported environments.
 *
 * @private
 * @param environment An environment name.
 * @returns Returns `true` if the `environment` is one of the supported environments.
 */
const isKnownEnvironment = (environment: string | undefined) => {
  return environment !== undefined && Object.values<string>(knownEnvironments).includes(environment)
}

/**
 * The current application environment set by `VUE_APP_ENV`.
 */
export const environment = process.env.VUE_APP_ENV?.trim().toLowerCase()

/**
 * Whether the app is running in a `'production'` environment or not.
 */
export const isProduction = environment === knownEnvironments.production

/**
 * Whether the app is running in a `'playground'` environment or not.
 */
export const isPlayground = environment === knownEnvironments.playground

/**
 * Whether the app is running in a `'nonprod'` environment or not.
 */
export const isNonProd = environment === knownEnvironments.nonprod

/**
 * Whether the app is running in a `'performance'` environment or not.
 */
export const isPerformance = environment === knownEnvironments.performance

/**
 * Whether the app is running in a `'development'` environment or not.
 */
export const isDevelopment = environment === knownEnvironments.development

/**
 * Whether the app is running in a `'test'` environment or not (as determined by `NODE_ENV`).
 */
export const isTest = process.env.NODE_ENV?.trim() === 'test'

if (!isTest && !isKnownEnvironment(environment)) {
  console.warn(
    `Unrecognized VUE_APP_ENV: expected one of ${Object.values(knownEnvironments).join(', ')} (got ${
      environment ?? '<empty>'
    })`
  )
}
